import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatKarachiDateTime, karachiBusinessDate } from '@/lib/dates'
import { formatPKR } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { buildTransactions, rangeTotals, type TxnType } from './transactions'

const selCls =
  'rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200 focus:border-sky-500 focus:outline-none'

type RangePreset = 'today' | '7d' | '30d' | 'all'

function rangeKeys(preset: RangePreset): { from?: string; to?: string } {
  const to = karachiBusinessDate()
  if (preset === 'today') return { from: to, to }
  if (preset === '7d') return { from: karachiBusinessDate(Date.now() - 6 * 86400000), to }
  if (preset === '30d') return { from: karachiBusinessDate(Date.now() - 29 * 86400000), to }
  return {}
}

/** Day-book: chronological purchases / sales / payments with type + date-range filters. */
export function TransactionsPage() {
  const ws = getActiveWorkspaceId()

  const purchases = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).purchases.where('workspace_id').equals(ws).toArray() : []), [ws])
  const sales = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).sales.where('workspace_id').equals(ws).toArray() : []), [ws])
  const payments = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).payments.where('workspace_id').equals(ws).toArray() : []), [ws])
  const devices = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).devices.where('workspace_id').equals(ws).toArray() : []), [ws])
  const contacts = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).contacts.where('workspace_id').equals(ws).toArray() : []), [ws])

  const [typeFilter, setTypeFilter] = useState<'all' | TxnType>('all')
  const [range, setRange] = useState<RangePreset>('30d')

  const inputs = {
    purchases: purchases ?? [],
    sales: sales ?? [],
    payments: payments ?? [],
    devices: devices ?? [],
    contacts: contacts ?? [],
  }
  const allRows = buildTransactions(inputs)
  const { from, to } = rangeKeys(range)
  const rows = allRows.filter((r) => (typeFilter === 'all' ? true : r.type === typeFilter))
  const totals = rangeTotals(inputs, rows, from, to)
  const shown = rows.filter((r) => (!from || r.dateKey >= from) && (!to || r.dateKey <= to))

  const badge: Record<TxnType, string> = {
    purchase: 'bg-slate-700/40 text-slate-300',
    sale: 'bg-emerald-500/15 text-emerald-400',
    payment: 'bg-sky-500/15 text-sky-400',
  }

  return (
    <Page title="Transactions" note="Day-book: value vs cash kept separate.">
      <div className="mb-3 grid grid-cols-2 gap-2">
        <Stat label="Sales value" value={formatPKR(totals.salesValue)} tone="text-emerald-400" />
        <Stat label="Purchases" value={formatPKR(totals.purchaseValue)} tone="text-slate-100" />
        <Stat label="Cash in" value={formatPKR(totals.cashIn)} tone="text-emerald-400" />
        <Stat label="Cash out" value={formatPKR(totals.cashOut)} tone="text-rose-400" />
      </div>

      <div className="mb-3 flex items-center gap-2">
        <select className={selCls} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)} aria-label="Type filter">
          <option value="all">All types</option>
          <option value="sale">Sales</option>
          <option value="purchase">Purchases</option>
          <option value="payment">Payments</option>
        </select>
        <select className={selCls} value={range} onChange={(e) => setRange(e.target.value as RangePreset)} aria-label="Date range">
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="all">All time</option>
        </select>
        <Link to="/receivables" className="ml-auto text-xs font-semibold text-amber-400 underline underline-offset-2">
          Udhaar / who owes me
        </Link>
      </div>

      {shown.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-700 px-4 py-8 text-center text-sm text-slate-500">
          No transactions in this range.
        </p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/40">
          {shown.map((r) => (
            <li key={`${r.type}-${r.id}`} className="flex items-center justify-between px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-100">
                  <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${badge[r.type]}`}>{r.type}</span>
                  {r.label}
                </p>
                <p className="text-[11px] text-slate-500">
                  {formatKarachiDateTime(r.at)}
                  {r.detail ? ` • ${r.detail}` : ''}
                </p>
              </div>
              <span className={`shrink-0 text-sm font-semibold tabular-nums ${r.direction === 'in' ? 'text-emerald-400' : 'text-rose-400'}`}>
                {r.direction === 'in' ? '+' : '−'}
                {formatPKR(r.amountPaisa, { symbol: false })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Page>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-0.5 text-base font-semibold tabular-nums ${tone}`}>{value}</p>
    </div>
  )
}
